import { invariant } from "@olenbetong/appframe-core";

import { IClient, getDefaultClient } from "./Client.js";
import { DataObject } from "./DataObject.js";
import { EventTarget } from "./events.js";
import { handleResponseError } from "./utils/handleResponseError.js";

type FileUploaderOptions =
	| {
			articleId?: string;
			dataObject: DataObject<any>;
			fieldName?: string;
			primKey?: string;
			route?: undefined;
			client?: IClient | null;
	  }
	| {
			articleId?: undefined;
			dataObject: undefined;
			fieldName?: undefined;
			primKey?: undefined;
			route: string;
			client?: IClient | null;
	  };

export type FileUploaderEventToValueMap = {
	onBeforeUpload: null;
	onUploaded: null;
	onUploadFailed: { error: unknown };
	onProgress: {
		loaded: number;
		total: number;
	};
};

export type FileUploaderEvent = keyof FileUploaderEventToValueMap;

export const FileUploaderEvents: Record<FileUploaderEvent, { abortable: boolean }> = {
	onBeforeUpload: { abortable: true },
	onUploaded: { abortable: false },
	onUploadFailed: { abortable: false },
	onProgress: { abortable: false },
};

export class FileUploader extends EventTarget<FileUploaderEventToValueMap> {
	private articleId: string;
	private dataObject: DataObject<any> | string | undefined;
	private fieldName: string | undefined;
	private primKey: string | undefined;
	private options: FileUploaderOptions;
	get client(): IClient {
		return this.options.client ?? getDefaultClient();
	}
	set client(value: IClient | null) {
		this.options.client = value;
	}

	constructor(options: FileUploaderOptions) {
		super(FileUploaderEvents);
		this.articleId = options.articleId ?? globalThis.af?.article?.id;
		this.dataObject = options.dataObject;
		this.fieldName = options.fieldName;
		this.primKey = options.primKey;
		this.options = { ...options };
	}

	private getDataSourceId() {
		return typeof this.dataObject === "string" ? this.dataObject : this.dataObject?.getDataSourceId();
	}

	getUploadUrl(): string {
		let url = this.options.route ?? `/file/upload/${this.articleId}/${this.getDataSourceId()}`;

		if (this.fieldName) {
			url += "/" + this.fieldName;
		}

		if (this.primKey) {
			url += "/" + this.primKey;
		}

		return url;
	}

	async upload(fileOrBlob: File | Blob, fields?: Record<string, any>, fileName?: string): Promise<unknown> {
		this.fireEvent("onBeforeUpload", null);

		const masterCriteria =
			typeof this.dataObject === "string"
				? {}
				: (this.dataObject?.getParameter("masterChildCriteria") as Record<string, string | Blob>);

		if (fields?.["PrimKey"]) {
			this.primKey = fields.PrimKey;
			delete fields.PrimKey;
		}

		const allFields = {
			...masterCriteria,
			...fields,
		};

		if (globalThis.XMLHttpRequest) {
			const data = new globalThis.FormData();

			for (let [field, value] of Object.entries(allFields)) {
				data.append(field, value);
			}

			data.append(
				this.fieldName ?? "File",
				fileOrBlob,
				fileOrBlob instanceof File ? fileOrBlob.name : fileName || "file",
			);

			const reqPromise = new Promise((resolve, reject) => {
				const request = new XMLHttpRequest();

				request.upload.addEventListener("progress", (evt) => {
					this.fireEvent("onProgress", {
						loaded: evt.loaded,
						total: evt.total,
					});
				});

				request.addEventListener("readystatechange", () => {
					if (request.readyState === 4) {
						if (request.status === 200) {
							this.fireEvent("onUploaded", null);
							resolve(request.response);
						} else {
							try {
								const result = JSON.parse(request.response);

								invariant(!result.error, result.error);
								throw Error("Upload failed: " + request.status + " " + request.response);
							} catch (error) {
								this.fireEvent("onUploadFailed", {
									error,
								});
								reject(error);
							}
						}
					}
				});

				request.open("POST", this.getUploadUrl());
				request.setRequestHeader("Accept", "application/json");
				request.setRequestHeader("X-Requested-With", "XMLHttpRequest");
				request.send(data as FormData);
			});
			return await reqPromise;
		} else {
			try {
				const nodeFetchModule = "./node.js";
				const { FormData } = await import(/* @vite-ignore */ /* webpackIgnore: true */ nodeFetchModule);
				const data = new FormData();

				for (let [field, value] of Object.entries(allFields)) {
					data.append(field, value);
				}

				data.append(
					this.fieldName ?? "File",
					fileOrBlob,
					(fileOrBlob as any)?.["name"] ?? (fileOrBlob as any)?.["fileName"] ?? "file",
				);

				let url = this.getUploadUrl();
				let response = await this.client.afFetch(url, {
					method: "POST",
					headers: {
						Accept: "application/json",
					},
					body: data,
					isJson: false,
				});

				if (response.ok) {
					const result = (await response.json()) as
						| { success: any; error: undefined }
						| { success: false; error?: any };
					invariant(!result.error, result.error);
				} else {
					let { error } = await handleResponseError(response);
					throw Error(error);
				}
			} catch (error) {
				this.fireEvent("onUploadFailed", { error });
				throw error;
			}
		}
	}
}
